import { useEffect, useRef } from 'react';
import { useRouter, useRootNavigationState } from 'expo-router';
import { useSupabaseAuth } from '../../shared/contexts/SupabaseAuthContext';
import { isTransientServerError, useApiClient } from '../../../helpers/apiClient.helper';
import { API_ENDPOINTS } from '../../../constants/api';
import {
  clearPendingB2bJoinCode,
  getPendingB2bJoinCode,
} from '../helpers/pendingB2bJoin.helper';
import { getPostB2bOrgJoinPath } from '../helpers/b2bOrgJoinRedirect.helper';
import { logger } from '../../helpers/logger.helper';
import type { B2bJoinOrganizationResponse } from '../../accounts/types/b2bAccount.types';
import { ALREADY_IN_ANOTHER_ORG_ERROR } from '../../../appStyles/_joinDeepLink.constants';

/** Navigation stability delay — matches useDeepLinkHandler. */
const NAV_STABILITY_DELAY_MS = 300;
/**
 * Max retries covering two failure modes:
 *  1. Transient 5xx (ensureUserExists first-login race)
 *  2. HTTP 200 but worldCupJoin.ok === false (squad join race / autoProvision contention)
 */
const MAX_SHELL_JOIN_RETRIES = 2;
/** Delay between retries — covers the ~3–4 s ensureUserExists / squad-join settlement window. */
const SHELL_JOIN_RETRY_DELAY_MS = 4000;

interface ApiEnvelope<T> {
  success: boolean;
  error?: string;
  data?: T;
}

/**
 * Completes a pending B2B org join after sign-in when OAuth or post-auth routing
 * lands on `/app/` instead of `/join?join=CODE`. Invite links persist the code on
 * `/join` mount; this hook fires it from the authenticated app shell.
 */
export function usePendingB2bJoinHandler(): void {
  const { isLoaded, isSignedIn, isAnonymous } = useSupabaseAuth();
  const rootNavState = useRootNavigationState();
  const apiClient = useApiClient();
  const router = useRouter();
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;
  const inFlightRef = useRef(false);

  useEffect(() => {
    if (!isLoaded || !isSignedIn || isAnonymous) return;
    if (!rootNavState?.key) return;
    if (inFlightRef.current) return;

    const timer = setTimeout(() => {
      void (async () => {
        const joinCode = await getPendingB2bJoinCode();
        if (!joinCode) return;

        inFlightRef.current = true;

        /** Attempt the join; retries once on transient 5xx to cover the ensureUserExists race. */
        const doJoin = async (attempt: number): Promise<void> => {
          try {
            const res = await apiClientRef.current<ApiEnvelope<B2bJoinOrganizationResponse>>(
              API_ENDPOINTS.accounts.join,
              { method: 'POST', data: { joinCode } },
            );
            if (!res.data?.success) {
              const errMsg = res.data?.error || 'Could not join';
              if (errMsg === ALREADY_IN_ANOTHER_ORG_ERROR) {
                await clearPendingB2bJoinCode();
              }
              logger.warn('[usePendingB2bJoinHandler] join failed', { message: errMsg });
              inFlightRef.current = false;
              return;
            }
            // Org joined. If the linked squad join failed, retry once — the second attempt
            // re-tries the squad join after autoProvision / ensureUserExists has settled.
            const squadOk = res.data.data?.worldCupJoin == null || res.data.data.worldCupJoin.ok === true;
            if (!squadOk && attempt < MAX_SHELL_JOIN_RETRIES) {
              logger.warn('[usePendingB2bJoinHandler] squad join failed, retrying', { attempt: attempt + 1 });
              setTimeout(() => void doJoin(attempt + 1), SHELL_JOIN_RETRY_DELAY_MS);
              return; // keep inFlightRef.current = true during retry window
            }
            await clearPendingB2bJoinCode();
            router.replace(getPostB2bOrgJoinPath(res.data.data) as never);
            // inFlightRef intentionally left true — navigation underway.
          } catch (err) {
            if (isTransientServerError(err) && attempt < MAX_SHELL_JOIN_RETRIES) {
              logger.warn('[usePendingB2bJoinHandler] retrying join after 500', { attempt: attempt + 1 });
              setTimeout(() => void doJoin(attempt + 1), SHELL_JOIN_RETRY_DELAY_MS);
              return; // keep inFlightRef.current = true during retry window
            }
            logger.warn('[usePendingB2bJoinHandler] join request failed', {
              message: err instanceof Error ? err.message : String(err),
            });
            // Keep pending code for retry on next app open or navigation.
            inFlightRef.current = false;
          }
        };

        void doJoin(0);
      })();
    }, NAV_STABILITY_DELAY_MS);

    return () => clearTimeout(timer);
  }, [isLoaded, isSignedIn, isAnonymous, rootNavState?.key, router]);
}
